/* eslint-disable @typescript-eslint/no-explicit-any */
import { defineStore } from "pinia";
import { Header } from "@/types/header";
import { UserPasswordType } from "@/types/user_password";
import { API_URL as DEFAULT_API_URL } from "@/utils/urls";
import { normalizeApiUrl, siloProfileHistoryPostUrl, type ApiQueryParams } from "@/utils/apiPath";

/** Hosts whose URLs must not be rewritten (avoids mixed-content redirects). */
const EXTERNAL_API_HOSTS = new Set(['aismartfeed.pecsmart.cloud']);

function resolveRequestUrl(url: string): string {
  try {
    const { hostname } = new URL(url);
    if (EXTERNAL_API_HOSTS.has(hostname)) {
      return url;
    }
  } catch {
    // relative URL — apply normalization
  }
  return normalizeApiUrl(url);
}

const API_URL = process.env.VUE_APP_API_URL ?? DEFAULT_API_URL;

interface User {
  id: number;
  username: string;
}

export const useMainStore = defineStore("mainStore", {
  state: () => ({
    loading: false,
    user: null as User | null,
  }),
  actions: {
    setLoading(value: boolean): void {
      this.loading = value
    },
    async getToken(user_password_obj: UserPasswordType): Promise<any> {
      return await this.postOnAPI(
        `${API_URL}/auth/login/`,
        user_password_obj,
        { "Content-Type": "application/json" }
      )
    },
    async getEmployee(username: string) {
      return await this.getFromAPI(`${API_URL}/users/report/${username}`)
    },
    async isUserAllowed(data: any): Promise<boolean> {
      let response = await this.getToken(data)
      const token = response.key

      window.localStorage.setItem("Token", token);
      window.localStorage.setItem("username", data.username);
      response = await this.getEmployee(data.username);
      const employeeData = response[0];
      this.user = {
        ...employeeData,
        id: employeeData.user,
        username: data.username,
      };
      window.localStorage.setItem("user_pk", employeeData.user);
      if (employeeData.role !== "pecsmart_admin") throw new Error('User Not Allowed')
      return true
    },
    /**
     * Loads `user` from localStorage.username via GET users/report/{username}.
     * A Token alone does not hydrate the store; username must exist (e.g. after LoginPage / isUserAllowed).
     */
    async fetchUser() {
      if (this.user) {
        return this.user;
      }
      const username = window.localStorage.getItem("username");
      if (username) {
        try {
          const response = await this.getEmployee(username);
          const employeeData = response[0];
          this.user = {
            ...employeeData,
            id: employeeData.user,
            username: username,
          };
          return this.user;
        } catch (error) {
          console.error("Failed to fetch user:", error);
          window.localStorage.removeItem("Token");
          window.localStorage.removeItem("username");
          window.localStorage.removeItem("user_pk");
          this.user = null;
        }
      }
      return null;
    },
    async requestFromAPI(url: string, init?: RequestInit): Promise<any> {
      try {
        const response = await fetch(resolveRequestUrl(url), init);
        
        if (response.statusText === "No Content") return response;
        
        if (!response.ok) {
          const errorText = await response.text();
          const trimmed = errorText.trim();
          const isHtml =
            trimmed.startsWith('<!DOCTYPE html>') || trimmed.toLowerCase().startsWith('<html');
          // Login pages are often HTML on 401/403; Django DEBUG 500 pages are also HTML — do not call those "auth".
          if (isHtml && (response.status === 401 || response.status === 403)) {
            throw new Error('Authentication error. Your session may have expired. Please refresh the page and log in again.');
          }
          if (isHtml && response.status >= 500) {
            throw new Error(
              `Server error (${response.status}). The API returned an HTML error page — check backend logs (not a session expiry).`
            );
          }
          if (isHtml) {
            throw new Error(
              'The server returned HTML instead of JSON. Try logging in again or check the requested URL.'
            );
          }
          try {
            const errorJson = JSON.parse(errorText);
            throw new Error(`HTTP ${response.status}: ${response.statusText} - ${JSON.stringify(errorJson)}`);
          } catch {
            throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
          }
        }

        const text = await response.text();
        
        // Sanitize the text by removing leading/trailing whitespace and control characters
        const sanitizedText = text.trim();

        if (sanitizedText === '') {
          return null; // Return null for empty responses
        }

        try {
          return JSON.parse(sanitizedText);
        } catch (error) {
          console.error('JSON parsing error after sanitization:', error);
          console.error('Original response text:', text);
          console.error('Sanitized text:', sanitizedText);
          // Check if the sanitized text is HTML, which indicates an auth issue
          if (sanitizedText.includes('<!DOCTYPE html>')) {
            throw new Error('Authentication session may have expired. The server returned a login page instead of data. Please refresh the page.');
          }
          throw new Error(`Error parsing JSON response: ${error}`);
        }
      } catch (error: any) {
        // If it's a network or fetch error
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
          throw new Error('Connection error: Could not connect to server');
        }
        // If it's already an Error, re-throw it
        if (error instanceof Error) {
          throw error;
        }
        // For other types of errors
        throw new Error(`Unexpected error: ${error.message || error}`);
      }
    },
    /** POST silo_profile_history with canonical URL (trailing slash, stable query). */
    async getSiloProfileHistory(
      params: object,
      extraQuery: object = {},
    ): Promise<any> {
      const url = siloProfileHistoryPostUrl(API_URL, extraQuery as ApiQueryParams);
      return this.postOnAPI(url, params);
    },
    async getFromAPI(url: string, camv3: boolean = false, cache?: 'no-cache' | 'reload'): Promise<any> {
      const init: RequestInit = {
        headers: camv3 ? undefined : this.getHeader
      };
      
      if (cache) {
        init.cache = cache;
      }
      
      return await this.requestFromAPI(url, init)
    },
    async getFromAPICounter(url: string): Promise<any> {
      return await this.requestFromAPI(url, { headers: this.getHeaderCounter })
    },
    async postOnAPICounter(url: string, data: any, header?: Header): Promise<any> {
      return await this.requestFromAPI(url, {
        method: "POST",
        body: JSON.stringify(data),
        headers: header !== undefined ? header : this.getHeaderCounter,
      })
    },
    async postFormDataOnAPICounter(url: string, formData: FormData): Promise<any> {
      return await this.requestFromAPI(url, {
        method: "POST",
        body: formData,
        headers: {
          "X-API-Key": process.env.VUE_APP_KEY_COUNTER as string
        }
      })
    },
    async patchOnAPICounter(url: string, data: any, header?: Header, cache?: 'no-cache'): Promise<any> {
      const init: RequestInit = {
        method: "PATCH",
        body: JSON.stringify(data),
        headers: header !== undefined ? header : this.getHeaderCounter,
      };

      if (cache) {
        init.cache = cache;
      }

      const response = await this.requestFromAPI(url, init)
      return response
    },
    async getFromAPIPlane(url: string): Promise<any> {
      return await this.requestFromAPI(url, { headers: this.getHeaderPlane })
    },
    async postOnAPIPlane(url: string, data: any, header?: Header): Promise<any> {
      return await this.requestFromAPI(url, {
        method: "POST",
        body: JSON.stringify(data),
        headers: header !== undefined ? header : this.getHeaderPlane,
      })
    },
    async editFromAPIPlane(url: string, data: any): Promise<any> {
      return await this.requestFromAPI(url, { method: "PATCH", headers: this.getHeaderPlane, body: JSON.stringify(data) })
    },
    async deleteFromAPIPlane(url: string): Promise<any> {
      return await this.requestFromAPI(url, { method: "DELETE", headers: this.getHeaderPlane })
    },
    async getAllFromAPI(url: string, aggregatedResults: any[] = [], next_field_present = true, n_page = 1, cache?: 'no-cache'): Promise<any[]> {
      try {
        const response = await this.getFromAPI(`${url}${next_field_present ? '' : '&page=' + n_page}`, false, cache);
        
        // Verificar que response.results existe y es un array
        if (!response || !response.results || !Array.isArray(response.results)) {
          console.warn('Invalid response format from API:', response);
          return aggregatedResults;
        }
        
        const newResults = [...aggregatedResults, ...response.results];

        if (next_field_present) {
          if (response.next) {
            // If 'next' field is available, use it
            const httpsNext = response.next.replace("http:", "https:");
            return await this.getAllFromAPI(httpsNext, newResults, true, 1, cache);
          }
        } else {
          if (response.results.length === 200) {
            // If 'next' field is not present, and 200 results are returned, proceed to next page manually
            return await this.getAllFromAPI(url, newResults, false, n_page + 1, cache);
          }
        }

        return newResults;
      } catch (error) {
        console.error('Error in getAllFromAPI:', error);
        // Si hay un error, devolver los resultados acumulados hasta ahora
        return aggregatedResults;
      }
    },
    async postOnAPI(url: string, data: any, header?: Header, cache?: 'no-cache'): Promise<any> {
      const init: RequestInit = {
        method: "POST",
        body: JSON.stringify(data),
        headers: header !== undefined ? header : this.getHeader,
      };
      
      if (cache) {
        init.cache = cache;
      }
      
      return await this.requestFromAPI(url, init)
    },
    async patchOnAPI(url: string, data: any, header?: Header, cache?: 'no-cache'): Promise<any> {
      const init: RequestInit = {
        method: "PATCH",
        body: JSON.stringify(data),
        headers: header !== undefined ? header : this.getHeader,
      };
      
      if (cache) {
        init.cache = cache;
      }
      
      const response = await this.requestFromAPI(url, init)
      return response
    },
    async deleteFromAPI(url: string, data: any): Promise<any> {
      return await this.requestFromAPI(url, { method: "DELETE", headers: this.getHeader, body: JSON.stringify(data) })
    },
    checkUserPK(): string {
      const user_pk: string | null = window.localStorage.getItem("user_pk")
      if (user_pk == null) throw "No user pk, Refresh page"
      return user_pk
    }
  },
  getters: {
    getHeader(): Header {
      return {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "Authorization": `Token ${window.localStorage.getItem("Token")}`,
      };
    },
    getHeaderPlane(): Header {
      return {
        "Content-Type": "application/json",
        "X-API-Key": process.env.VUE_APP_KEY_PLANE,
      };
    },
    getHeaderCounter(): Header {
      return {
        "Content-Type": "application/json",
        "X-API-Key": process.env.VUE_APP_KEY_COUNTER,
      };
    },
    getLoading: state => state.loading
  },
});

